"use client";

import type { ChatCitation } from "@atlas/ai";
import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import "./SourceChip.css";

const REMARK_PLUGINS = [remarkGfm, remarkBreaks];

/** Milliseconds to wait before closing, giving the cursor time to reach the popover. */
const HIDE_DELAY_MS = 150;

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

export interface SourceChipProps {
  /** The citation data to display. */
  cite: ChatCitation;
  /**
   * Optional 1-based index shown as `[N]` prefix. When provided the chip
   * shows a bracketed display number before the label.
   */
  displayNum?: string;
  /**
   * When provided, overrides `cite.sourceUrls?.[0]` as the primary URL shown
   * in the popover. Use this when the same `ChatCitation` object is shared
   * across multiple slots (one per source URL) so each chip links to its own
   * distinct URL rather than always falling back to the first URL.
   */
  primaryUrlOverride?: string;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/** Label shown for citations sourced from the Elion private vault. */
const ELION_PRIVATE_DATA_LABEL = "Elion private data";

/** Strip any trailing AI-provider tag (e.g. ": perplexity", " — vertex") from a
 *  raw document title so we never surface those names in attribution UI. */
function sanitizeDocumentTitle(title: string): string {
  return title.replace(/\s*[:—-]\s*(perplexity|vertex)\b.*$/i, "").trim();
}

/** Hostname (sans leading www.) for a URL — used as a public source label.
 *  Falls back to the raw string for non-URL refs. */
function hostnameLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Inline citation chip used in chat and report contexts.
 *
 * Shows a compact tag with the source label. On hover/focus, a portal-based
 * popover surfaces the full citation detail: label, source URL, and synthesis.
 *
 * Internal citations (`cite.isInternal === true`) render without a link and
 * display "Elion private data" instead of a URL. Provider names (Perplexity,
 * Vertex, etc.) are never exposed.
 */
export function SourceChip({
  cite,
  displayNum,
  primaryUrlOverride,
}: SourceChipProps) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [tipStyle, setTipStyle] = useState<CSSProperties | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const chipCls = cite.isInternal ? "c-chip cat-elion" : "c-chip cat-direct";
  const primaryUrl = primaryUrlOverride ?? cite.sourceUrls?.[0];
  const displayContent = cite.condensedContent;
  // Public citations: prefer the URL hostname as the chip label since the
  // upstream `documentTitle` is a generic prefix ("Research", "Organization
  // context research") that can also encode the AI provider — never user-
  // facing. Fall back to a sanitized title when there's no URL.
  const sanitizedTitle = sanitizeDocumentTitle(cite.documentTitle);
  const displayLabel = cite.isInternal
    ? ELION_PRIVATE_DATA_LABEL
    : primaryUrl
      ? hostnameLabel(primaryUrl)
      : sanitizedTitle || "Source";

  function show() {
    // Cancel any pending hide so re-entering the chip keeps the popover open.
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    setTipStyle({
      position: "fixed",
      bottom: window.innerHeight - r.top + 8,
      left: Math.min(window.innerWidth - 300, Math.max(12, r.left)),
      zIndex: 1000,
    });
  }

  /** Immediately close – used when leaving the popover itself. */
  function hide() {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setTipStyle(null);
  }

  /**
   * Schedule a hide after a short delay.  If the cursor reaches the popover
   * (or returns to the chip) before the timer fires, `show` / `cancelHide`
   * will clear it and the popover stays visible.
   */
  function scheduleHide() {
    if (hideTimerRef.current !== null) return; // already scheduled
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      setTipStyle(null);
    }, HIDE_DELAY_MS);
  }

  function cancelHide() {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }

  useLayoutEffect(() => {
    if (!tipStyle) return;
    function reposition() {
      show();
    }
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [tipStyle]);

  // Clean up the timer if the component unmounts while a hide is pending.
  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!tipStyle) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") hide();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tipStyle]);

  const popover = tipStyle
    ? createPortal(
        // Mouse events on the wrapper keep the popover alive while the cursor
        // is inside it, and close it once the cursor leaves.
        <div
          style={tipStyle}
          onMouseEnter={cancelHide}
          onMouseLeave={hide}
          // Focus events: keep open when focus moves into the popover (e.g.
          // keyboard-navigating to the citation link), close when focus leaves.
          onFocusCapture={cancelHide}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              hide();
            }
          }}
        >
          <div className="src-pop" role="tooltip">
            <span className="src-pop-eyebrow">
              {cite.isInternal ? ELION_PRIVATE_DATA_LABEL : "Direct source"}
            </span>
            <span className="src-pop-title">{displayLabel}</span>
            {primaryUrl && !cite.isInternal && (
              <a
                href={primaryUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="src-pop-url"
                onClick={(e) => e.stopPropagation()}
              >
                {primaryUrl}
              </a>
            )}
            {displayContent && (
              <div className="src-pop-relevance">
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                  {displayContent}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        className={chipCls}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
      >
        {displayNum && <span className="c-num">[{displayNum}]</span>}
        <span className="c-dot" aria-hidden="true" />
        <span className="c-lbl">{displayLabel}</span>
      </button>
      {popover}
    </>
  );
}
